import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ContentContainer, Alert, Spinner } from '@surfline/quiver-react';
import { trackNavigatedToPage, getUserDetails, getEntitlements } from '@surfline/web-common';
import { withRouter } from 'next/router';
import PremiumAnalysisMeta from '../../components/PremiumAnalysisMeta';
import FeedList from '../../components/FeedList';
import { getSubregionOverviewData } from '../../selectors/subregion';
import getOrderedArticles from '../../utils/getOrderedArticles';
import { subregionForecastAdBaseConfig } from '../../utils/adConfig';
import userDetailsPropType from '../../propTypes/userDetails';
import overviewDataPropTypes from '../../propTypes/subregionOverviewData';
import articlePropTypes from '../../propTypes/article';
import { getUserIsEntitled } from '../../selectors/user';
import { SL_PREMIUM_FORECASTS } from '../../common/permissions';
import {
  renderJWElements,
  renderTwitterElements,
  renderIgElements,
  renderCameraEmbeds,
  unmountJWElements,
} from '../../utils/renderCustomComponents';

class PremiumAnalysis extends Component {
  componentDidMount() {
    const {
      isPremium,
      jwPlayerLoaded,
      overviewData: { name, _id, forecastSummary },
      trackPage,
    } = this.props;

    if (trackPage !== false) {
      trackNavigatedToPage('Premium Analysis', {
        category: 'forecast',
        channel: 'forecast',
        human:
          forecastSummary?.forecastStatus?.status === 'active' &&
          (forecastSummary?.hasHighlights || forecastSummary?.hasBets),
        subregionId: _id,
        subregionName: name,
        viewedLocation: 'regional',
      });
    }
    renderTwitterElements();
    renderIgElements();
    if (jwPlayerLoaded) {
      renderCameraEmbeds(isPremium);
    }
    renderJWElements();
  }

  componentDidUpdate(prevProps) {
    const { isPremium, jwPlayerLoaded } = this.props;

    if (!prevProps.jwPlayerLoaded && jwPlayerLoaded) {
      renderCameraEmbeds(isPremium);
    }
  }

  componentWillUnmount() {
    unmountJWElements();
  }

  PremiumAnalysisContent = ({
    feed,
    articles,
    overviewData,
    isPremium,
    subregionId,
    subregionAdConfig,
  }) => (
    <div className="sl-premium-analysis__content">
      {feed.loading && <Spinner />}
      {feed.error && <Spinner error />}
      {feed.articles.length > 0 && (
        <FeedList
          articles={articles}
          subregionAdConfig={subregionAdConfig}
          isPremium={isPremium}
          subregionName={overviewData.name}
          subregionId={subregionId}
          showFullArticle
        />
      )}
      {feed.articles.length === 0 && !feed.loading && !feed.error && (
        <div className="sl-premium-analysis__message">
          <Alert>
            Premium Analysis currently unavailable for this region.
            <a href="mailto:forecast_feedback@surfline.com">Feedback?</a>
          </Alert>
        </div>
      )}
    </div>
  );

  render() {
    const { feed, isPremium, userDetails, entitlements, overviewData, router } = this.props;
    const { subregionId } = router.query;
    const subregionAdConfig = subregionForecastAdBaseConfig(
      subregionId,
      entitlements,
      !!userDetails,
    );
    const articles = feed.articles.length ? getOrderedArticles(feed.articles) : [];
    const [firstArticle] = articles;
    const headline = firstArticle?.content?.title;
    const author = firstArticle?.author?.name;

    return (
      <div className="sl-premium-analysis">
        <PremiumAnalysisMeta
          subregionName={overviewData.name}
          subregionId={subregionId}
          timestamp={overviewData.timestamp}
          headline={headline}
          author={author}
        />
        <ContentContainer>
          {this.PremiumAnalysisContent({
            feed,
            articles,
            overviewData,
            subregionId,
            isPremium,
            subregionAdConfig,
          })}
        </ContentContainer>
      </div>
    );
  }
}

PremiumAnalysis.defaultProps = {
  entitlements: [],
  isPremium: null,
  overviewData: null,
  trackPage: true,
  userDetails: null,
};

PremiumAnalysis.propTypes = {
  jwPlayerLoaded: PropTypes.bool.isRequired,
  isPremium: PropTypes.bool,
  userDetails: userDetailsPropType,
  entitlements: PropTypes.arrayOf(PropTypes.string),
  router: PropTypes.shape({
    query: PropTypes.shape({
      subregionId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  overviewData: overviewDataPropTypes,
  feed: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    articles: PropTypes.arrayOf(articlePropTypes),
  }).isRequired,
  trackPage: PropTypes.bool,
};

export default connect((state) => ({
  overviewData: getSubregionOverviewData(state),
  isPremium: getUserIsEntitled(state, SL_PREMIUM_FORECASTS),
  userDetails: getUserDetails(state),
  entitlements: getEntitlements(state),
  feed: state.subregion.feed,
}))(withRouter(PremiumAnalysis));
